import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Settings, Plus, Trash2, Save, Edit3, Check, X,
  Users, Receipt, Calculator, TrendingUp, AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import { confirmAction } from "@/lib/confirm";

const CHARGE_CATEGORIES = [
  { value: "loyer", label: "Loyer / dépôt" },
  { value: "assurance", label: "Assurances" },
  { value: "vehicules", label: "Véhicules / leasing" },
  { value: "telephone", label: "Téléphonie / internet" },
  { value: "logiciels", label: "Logiciels / abonnements" },
  { value: "divers", label: "Divers" },
];

const fmtEUR = (n) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(
    Number.isFinite(n) ? n : 0
  );

export default function ModuleSetup() {
  const { company, refresh } = useAuth();

  const [companyForm, setCompanyForm] = useState({
    name: "",
    trade: "peinture",
    siret: "",
    address: "",
    postal_code: "",
    city: "",
    phone: "",
    email: "",
    target_margin_pct: 30,
    legal_form: "",
    share_capital: "",
    rcs_city: "",
    vat_number: "",
    ape_code: "",
    insurance_decennale: "",
    rib_bank: "",
    rib_iban: "",
    rib_bic: "",
    rib_holder: "",
    cgv_text: "",
    quote_footer_notes: "",
    invoice_footer_notes: "",
    logo_data_uri: "",
    tax_regime: "IS",
    initial_treasury_balance: "",
    initial_treasury_date: "",
  });
  const [savingCompany, setSavingCompany] = useState(false);

  const [charges, setCharges] = useState([]);
  const [newCharge, setNewCharge] = useState({ label: "", amount_monthly: "", category: "loyer" });
  const [editingChargeId, setEditingChargeId] = useState(null);
  const [editChargeBuf, setEditChargeBuf] = useState(null);

  const [employees, setEmployees] = useState([]);
  const [newEmployee, setNewEmployee] = useState({
    name: "",
    role: "",
    gross_salary_monthly: "",
    employer_charges_pct: 42,
    hours_per_week: 35,
    vacation_days: 25,
    employment_type: "salarie",
    is_productive: true,
  });
  const [editingEmpId, setEditingEmpId] = useState(null);
  const [editEmpBuf, setEditEmpBuf] = useState(null);

  const [costEngine, setCostEngine] = useState(null);
  const [marginSlider, setMarginSlider] = useState(30);

  // Initial load
  useEffect(() => {
    (async () => {
      if (company) {
        setCompanyForm({
          name: company.name || "",
          trade: company.trade || "peinture",
          siret: company.siret || "",
          address: company.address || "",
          postal_code: company.postal_code || "",
          city: company.city || "",
          phone: company.phone || "",
          email: company.email || "",
          target_margin_pct: company.target_margin_pct ?? 30,
          legal_form: company.legal_form || "",
          share_capital: company.share_capital ?? "",
          rcs_city: company.rcs_city || "",
          vat_number: company.vat_number || "",
          ape_code: company.ape_code || "",
          insurance_decennale: company.insurance_decennale || "",
          rib_bank: company.rib_bank || "",
          rib_iban: company.rib_iban || "",
          rib_bic: company.rib_bic || "",
          rib_holder: company.rib_holder || "",
          cgv_text: company.cgv_text || "",
          quote_footer_notes: company.quote_footer_notes || "",
          invoice_footer_notes: company.invoice_footer_notes || "",
          logo_data_uri: company.logo_data_uri || "",
          tax_regime: company.tax_regime || "IS",
          initial_treasury_balance: company.initial_treasury_balance ?? "",
          initial_treasury_date: company.initial_treasury_date || "",
        });
        setMarginSlider(company.target_margin_pct ?? 30);
      }
      await Promise.all([loadCharges(), loadEmployees(), loadCostEngine()]);
    })();
    // eslint-disable-next-line
  }, [company?.company_id]);

  const loadCharges = async () => {
    const { data } = await api.get("/charges");
    setCharges(data);
  };
  const loadEmployees = async () => {
    const { data } = await api.get("/employees");
    setEmployees(data);
  };
  const loadCostEngine = async () => {
    const { data } = await api.get("/cost-engine");
    setCostEngine(data);
  };

  // ----- Company -----
  const saveCompany = async () => {
    setSavingCompany(true);
    try {
      const payload = {
        ...companyForm,
        target_margin_pct: Number(companyForm.target_margin_pct) || 0,
        share_capital: companyForm.share_capital === "" ? null : Number(companyForm.share_capital) || 0,
        initial_treasury_balance: companyForm.initial_treasury_balance === "" ? null : Number(companyForm.initial_treasury_balance) || 0,
      };
      await api.patch("/company", payload);
      await refresh();
      await loadCostEngine();
      toast.success("Entreprise mise à jour");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSavingCompany(false);
    }
  };

  // ----- Charges -----
  const addCharge = async () => {
    if (!newCharge.label.trim() || !Number(newCharge.amount_monthly)) {
      toast.error("Libellé et montant requis");
      return;
    }
    await api.post("/charges", {
      label: newCharge.label.trim(),
      amount_monthly: Number(newCharge.amount_monthly),
      category: newCharge.category,
    });
    setNewCharge({ label: "", amount_monthly: "", category: "loyer" });
    await Promise.all([loadCharges(), loadCostEngine()]);
    toast.success("Charge ajoutée");
  };

  const startEditCharge = (c) => { setEditingChargeId(c.id); setEditChargeBuf({ ...c }); };
  const cancelEditCharge = () => { setEditingChargeId(null); setEditChargeBuf(null); };
  const saveEditCharge = async () => {
    await api.patch(`/charges/${editingChargeId}`, {
      label: editChargeBuf.label,
      amount_monthly: Number(editChargeBuf.amount_monthly),
      category: editChargeBuf.category,
    });
    setEditingChargeId(null);
    setEditChargeBuf(null);
    await Promise.all([loadCharges(), loadCostEngine()]);
    toast.success("Charge mise à jour");
  };
  const removeCharge = async (id) => {
    if (!(await confirmAction("Supprimer cette charge ?"))) return;
    await api.delete(`/charges/${id}`);
    await Promise.all([loadCharges(), loadCostEngine()]);
  };

  // ----- Employees -----
  const addEmployee = async () => {
    if (!newEmployee.name.trim() || !Number(newEmployee.gross_salary_monthly)) {
      toast.error("Nom et salaire brut requis");
      return;
    }
    await api.post("/employees", {
      ...newEmployee,
      name: newEmployee.name.trim(),
      gross_salary_monthly: Number(newEmployee.gross_salary_monthly),
      employer_charges_pct: Number(newEmployee.employer_charges_pct),
      hours_per_week: Number(newEmployee.hours_per_week),
      vacation_days: Number(newEmployee.vacation_days),
      is_productive: !!newEmployee.is_productive,
    });
    setNewEmployee({ name: "", role: "", gross_salary_monthly: "", employer_charges_pct: 42, hours_per_week: 35, vacation_days: 25, employment_type: "salarie", is_productive: true });
    await Promise.all([loadEmployees(), loadCostEngine()]);
    toast.success("Salarié ajouté");
  };
  const startEditEmp = (e) => { setEditingEmpId(e.id); setEditEmpBuf({ ...e }); };
  const cancelEditEmp = () => { setEditingEmpId(null); setEditEmpBuf(null); };
  const saveEditEmp = async () => {
    await api.patch(`/employees/${editingEmpId}`, {
      name: editEmpBuf.name,
      role: editEmpBuf.role,
      gross_salary_monthly: Number(editEmpBuf.gross_salary_monthly),
      employer_charges_pct: Number(editEmpBuf.employer_charges_pct),
      hours_per_week: Number(editEmpBuf.hours_per_week),
      vacation_days: Number(editEmpBuf.vacation_days),
      employment_type: editEmpBuf.employment_type || "salarie",
      is_productive: editEmpBuf.is_productive !== false,
    });
    setEditingEmpId(null);
    setEditEmpBuf(null);
    await Promise.all([loadEmployees(), loadCostEngine()]);
    toast.success("Salarié mis à jour");
  };
  const removeEmployee = async (id) => {
    if (!(await confirmAction("Supprimer ce salarié ?"))) return;
    await api.delete(`/employees/${id}`);
    await Promise.all([loadEmployees(), loadCostEngine()]);
  };

  // ----- Margin slider live -----
  const sellRateLive = useMemo(() => {
    if (!costEngine) return 0;
    return costEngine.hourly_break_even * (1 + marginSlider / 100);
  }, [costEngine, marginSlider]);

  const applyMargin = async () => {
    await api.patch("/company", { target_margin_pct: Number(marginSlider) });
    await refresh();
    await loadCostEngine();
    toast.success(`Marge cible enregistrée à ${marginSlider}%`);
  };

  const isReady = (costEngine?.charges_count || 0) > 0 && (costEngine?.employees_count || 0) > 0;

  return (
    <div data-testid="module-setup" className="max-w-7xl mx-auto">
      {/* Header */}
      <header className="mb-8 lg:mb-12">
        <div className="flex items-center gap-2 text-xs font-semibold text-[#FFC400] mb-3">
          <Settings size={13} /> Module 1 — Configuration & Coûts
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] text-primary-text">
          {company?.name || "Votre entreprise"}
        </h1>
        <p className="text-secondary-text mt-3 max-w-2xl">
          Renseignez les charges fixes mensuelles <b>HT</b> et l'équipe. Votre <b>taux de facturation horaire HT minimum</b> est recalculé en direct.
        </p>
      </header>

      {/* Bandeau mode auto-entrepreneur */}
      {costEngine?.micro_mode && (
        <div className="rounded-lg p-4 mb-6" style={{ background: "rgba(255,196,0,0.08)", border: "1px solid rgba(255,196,0,0.30)" }} data-testid="micro-mode-banner">
          <div className="text-sm leading-relaxed">
            <span className="font-semibold" style={{ color: "#FFC400" }}>Mode auto-entrepreneur actif</span>
            <span style={{ color: "var(--chalk)" }}>
              {" "}— vos cotisations URSSAF ({String(costEngine.micro_charges_pct).replace(".", ",")} % du CA) sont intégrées :
              coût de revient {fmtEUR(costEngine.hourly_break_even_base)}/h → seuil réel {fmtEUR(costEngine.hourly_break_even)}/h.
              La section Salariés est remplacée par « Votre rémunération » ci-dessous : indiquez simplement le salaire que vous visez.
            </span>
            <Link to="/profile" className="ml-1 font-semibold hover:underline" style={{ color: "#FFC400" }}>Modifier le taux →</Link>
          </div>
        </div>
      )}

      {/* Cost Engine KPI strip */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiCard
          accent
          label="Point mort taux horaire HT"
          value={costEngine ? fmtEUR(costEngine.hourly_break_even) : "—"}
          hint={costEngine?.micro_mode
            ? `Dont cotisations URSSAF ${String(costEngine.micro_charges_pct).replace(".", ",")} % (coût brut ${fmtEUR(costEngine.hourly_break_even_base)})`
            : "Coût de revient / heure productive"}
          icon={Calculator}
          testid="kpi-hourly-break-even"
        />
        <KpiCard
          label="Taux de vente conseillé HT"
          value={costEngine ? fmtEUR(costEngine.suggested_sell_rate) : "—"}
          hint={`Marge ${marginSlider}%`}
          icon={TrendingUp}
          testid="kpi-suggested-sell-rate"
        />
        <KpiCard
          label="Coût mensuel total HT"
          value={costEngine ? fmtEUR(costEngine.total_monthly_cost) : "—"}
          hint={`${costEngine?.charges_count || 0} charges + ${costEngine?.employees_count || 0} salariés`}
          icon={Receipt}
          testid="kpi-total-cost"
        />
        <KpiCard
          label="Heures productives / mois"
          value={costEngine ? `${costEngine.total_productive_hours_monthly} h` : "—"}
          hint="Total équipe (hors congés)"
          icon={Users}
          testid="kpi-productive-hours"
        />
      </div>

      {/* Margin slider live preview */}
      <section className="card p-6 mb-8" data-testid="margin-slider-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
          <div>
            <h3 className="font-semibold text-lg">Marge cible & taux de vente</h3>
            <p className="text-sm text-secondary-text">Ajustez votre marge — votre taux de vente conseillé se met à jour en temps réel.</p>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="numerals text-4xl" style={{ color: "#FFC400" }}>{fmtEUR(sellRateLive)}</span>
            <span className="text-sm text-secondary-text">/ h facturée HT</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={marginSlider}
            onChange={(e) => setMarginSlider(Number(e.target.value))}
            className="flex-1 accent-[#FFC400]"
            data-testid="margin-slider"
          />
          <span className="numerals text-sm w-12 text-right">{marginSlider}%</span>
          <button onClick={applyMargin} className="btn btn-outline btn-sm" data-testid="apply-margin">
            <Save size={14} /> Appliquer
          </button>
        </div>
        {!isReady && (
          <div className="mt-5 flex items-start gap-3 p-3 rounded-lg bg-[rgba(255,196,0,0.08)] border border-[rgba(255,196,0,0.35)] text-sm text-[#FFC400]">
            <AlertCircle size={18} className="shrink-0 mt-0.5" />
            <span>Ajoutez au moins une charge fixe et un salarié pour calculer votre coût horaire réel.</span>
          </div>
        )}
      </section>

      {/* Fixed charges */}
      <section className="card p-6 mb-8 animate-in delay-1" data-testid="charges-card">
        <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[var(--carbon-3)]/8 text-[var(--carbon-3)] flex items-center justify-center">
              <Receipt size={18} />
            </div>
            <div>
              <h2 className="font-semibold text-xl">Charges fixes mensuelles</h2>
              <p className="text-xs text-secondary-text mt-0.5">{charges.length} {charges.length > 1 ? "charges" : "charge"} · {fmtEUR(costEngine?.total_fixed_monthly || 0)} HT / mois</p>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Libellé</th>
                <th>Catégorie</th>
                <th className="text-right">Montant mensuel HT</th>
                <th className="w-32 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {charges.map((c) => (
                <tr key={c.id} data-testid={`charge-${c.id}`}>
                  {editingChargeId === c.id ? (
                    <>
                      <td><input className="input" value={editChargeBuf.label} onChange={(e) => setEditChargeBuf({ ...editChargeBuf, label: e.target.value })} /></td>
                      <td>
                        <select className="input" value={editChargeBuf.category} onChange={(e) => setEditChargeBuf({ ...editChargeBuf, category: e.target.value })}>
                          {CHARGE_CATEGORIES.map((cat) => <option key={cat.value} value={cat.value}>{cat.label}</option>)}
                        </select>
                      </td>
                      <td><input type="number" step="0.01" className="input text-right numerals" value={editChargeBuf.amount_monthly} onChange={(e) => setEditChargeBuf({ ...editChargeBuf, amount_monthly: e.target.value })} /></td>
                      <td className="text-right">
                        <button onClick={saveEditCharge} className="btn btn-ghost btn-sm" data-testid={`charge-save-${c.id}`}><Check size={14} /></button>
                        <button onClick={cancelEditCharge} className="btn btn-ghost btn-sm"><X size={14} /></button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="font-medium">{c.label}</td>
                      <td><span className="badge badge-muted">{CHARGE_CATEGORIES.find((cat) => cat.value === c.category)?.label || c.category}</span></td>
                      <td className="text-right numerals">{fmtEUR(c.amount_monthly)}</td>
                      <td className="text-right">
                        <button onClick={() => startEditCharge(c)} className="btn btn-ghost btn-sm" data-testid={`charge-edit-${c.id}`}><Edit3 size={14} /></button>
                        <button onClick={() => removeCharge(c.id)} className="btn btn-danger btn-sm" data-testid={`charge-delete-${c.id}`}><Trash2 size={14} /></button>
                      </td>
                    </>
                  )}
                </tr>
              ))}

              {/* New charge row */}
              <tr className="bg-[var(--carbon-3)]">
                <td><input className="input" placeholder="Ex: Loyer atelier" value={newCharge.label} onChange={(e) => setNewCharge({ ...newCharge, label: e.target.value })} data-testid="new-charge-label" /></td>
                <td>
                  <select className="input" value={newCharge.category} onChange={(e) => setNewCharge({ ...newCharge, category: e.target.value })} data-testid="new-charge-category">
                    {CHARGE_CATEGORIES.map((cat) => <option key={cat.value} value={cat.value}>{cat.label}</option>)}
                  </select>
                </td>
                <td><input type="number" step="0.01" className="input text-right numerals" placeholder="0,00 €" value={newCharge.amount_monthly} onChange={(e) => setNewCharge({ ...newCharge, amount_monthly: e.target.value })} data-testid="new-charge-amount" /></td>
                <td className="text-right">
                  <button onClick={addCharge} className="btn btn-accent btn-sm" data-testid="add-charge-btn"><Plus size={14} /> Ajouter</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Équipe : salariés (régime classique) OU rémunération simple (auto-entrepreneur) */}
      {costEngine?.micro_mode ? (
        <MicroRemunCard costEngine={costEngine} onSaved={loadCostEngine} />
      ) : (
      <section className="card p-6 mb-8 animate-in delay-2" data-testid="employees-card">
        <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[var(--carbon-3)]/8 text-[var(--carbon-3)] flex items-center justify-center">
              <Users size={18} />
            </div>
            <div>
              <h2 className="font-semibold text-xl">Salariés</h2>
              <p className="text-xs text-secondary-text mt-0.5">{employees.length} {employees.length > 1 ? "salariés" : "salarié"} · coût total {fmtEUR(costEngine?.total_employee_cost_monthly || 0)} HT / mois</p>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Statut</th>
                <th>Rôle</th>
                <th className="text-center">Productif</th>
                <th className="text-right">Salaire / Rémun. brut</th>
                <th className="text-right">Charges %</th>
                <th className="text-right">h / sem</th>
                <th className="text-right">Congés (j)</th>
                <th className="text-right">Coût mensuel HT</th>
                <th className="w-32 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((e) => {
                const total = (e.gross_salary_monthly || 0) * (1 + (e.employer_charges_pct || 0) / 100);
                return (
                  <tr key={e.id} data-testid={`employee-${e.id}`}>
                    {editingEmpId === e.id ? (
                      <>
                        <td><input className="input" value={editEmpBuf.name} onChange={(ev) => setEditEmpBuf({ ...editEmpBuf, name: ev.target.value })} /></td>
                        <td>
                          <select className="input" value={editEmpBuf.employment_type || "salarie"} onChange={(ev) => setEditEmpBuf({ ...editEmpBuf, employment_type: ev.target.value, employer_charges_pct: ev.target.value === "tns" ? 45 : 42 })}>
                            <option value="salarie">Salarié</option>
                            <option value="tns">TNS (non salarié)</option>
                          </select>
                        </td>
                        <td><input className="input" value={editEmpBuf.role} onChange={(ev) => setEditEmpBuf({ ...editEmpBuf, role: ev.target.value })} /></td>
                        <td className="text-center">
                          <input type="checkbox" className="w-4 h-4 accent-[#FFC400]" checked={editEmpBuf.is_productive ?? true} onChange={(ev) => setEditEmpBuf({ ...editEmpBuf, is_productive: ev.target.checked })} title="Productif (compte dans les heures facturables)" />
                        </td>
                        <td><input type="number" className="input text-right numerals" value={editEmpBuf.gross_salary_monthly} onChange={(ev) => setEditEmpBuf({ ...editEmpBuf, gross_salary_monthly: ev.target.value })} /></td>
                        <td><input type="number" step="0.1" className="input text-right numerals" value={editEmpBuf.employer_charges_pct} onChange={(ev) => setEditEmpBuf({ ...editEmpBuf, employer_charges_pct: ev.target.value })} /></td>
                        <td><input type="number" step="0.5" className="input text-right numerals" value={editEmpBuf.hours_per_week} onChange={(ev) => setEditEmpBuf({ ...editEmpBuf, hours_per_week: ev.target.value })} /></td>
                        <td><input type="number" className="input text-right numerals" value={editEmpBuf.vacation_days} onChange={(ev) => setEditEmpBuf({ ...editEmpBuf, vacation_days: ev.target.value })} /></td>
                        <td className="text-right numerals">{fmtEUR(Number(editEmpBuf.gross_salary_monthly) * (1 + Number(editEmpBuf.employer_charges_pct) / 100))}</td>
                        <td className="text-right">
                          <button onClick={saveEditEmp} className="btn btn-ghost btn-sm"><Check size={14} /></button>
                          <button onClick={cancelEditEmp} className="btn btn-ghost btn-sm"><X size={14} /></button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="font-medium">{e.name}</td>
                        <td>
                          {e.employment_type === "tns" ? (
                            <span className="badge badge-accent">TNS</span>
                          ) : (
                            <span className="badge badge-muted">Salarié</span>
                          )}
                        </td>
                        <td className="text-secondary-text">{e.role || "—"}</td>
                        <td className="text-center">
                          {e.is_productive === false ? (
                            <span className="badge badge-muted" title="Compte dans les charges mais PAS dans les heures facturables">Non</span>
                          ) : (
                            <span className="badge" style={{ background: "rgba(255,196,0,0.15)", color: "#FFC400" }} title="Heures facturables comptées dans le point mort">Oui</span>
                          )}
                        </td>
                        <td className="text-right numerals">{fmtEUR(e.gross_salary_monthly)}</td>
                        <td className="text-right numerals">{e.employer_charges_pct}%</td>
                        <td className="text-right numerals">{e.hours_per_week}</td>
                        <td className="text-right numerals">{e.vacation_days}</td>
                        <td className="text-right numerals font-semibold">{fmtEUR(total)}</td>
                        <td className="text-right">
                          <button onClick={() => startEditEmp(e)} className="btn btn-ghost btn-sm" data-testid={`employee-edit-${e.id}`}><Edit3 size={14} /></button>
                          <button onClick={() => removeEmployee(e.id)} className="btn btn-danger btn-sm" data-testid={`employee-delete-${e.id}`}><Trash2 size={14} /></button>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}

              {/* New employee row */}
              <tr className="bg-[var(--carbon-3)]">
                <td><input className="input" placeholder="Nom & prénom" value={newEmployee.name} onChange={(e) => setNewEmployee({ ...newEmployee, name: e.target.value })} data-testid="new-emp-name" /></td>
                <td>
                  <select className="input" value={newEmployee.employment_type} onChange={(e) => setNewEmployee({ ...newEmployee, employment_type: e.target.value, employer_charges_pct: e.target.value === "tns" ? 45 : 42 })} data-testid="new-emp-type">
                    <option value="salarie">Salarié</option>
                    <option value="tns">TNS (non salarié)</option>
                  </select>
                </td>
                <td><input className="input" placeholder="Peintre, gérant..." value={newEmployee.role} onChange={(e) => setNewEmployee({ ...newEmployee, role: e.target.value })} data-testid="new-emp-role" /></td>
                <td className="text-center">
                  <input type="checkbox" className="w-4 h-4 accent-[#FFC400]" checked={newEmployee.is_productive ?? true} onChange={(e) => setNewEmployee({ ...newEmployee, is_productive: e.target.checked })} title="Productif (compte dans les heures facturables)" data-testid="new-emp-productive" />
                </td>
                <td><input type="number" className="input text-right numerals" placeholder="2000" value={newEmployee.gross_salary_monthly} onChange={(e) => setNewEmployee({ ...newEmployee, gross_salary_monthly: e.target.value })} data-testid="new-emp-salary" /></td>
                <td><input type="number" step="0.1" className="input text-right numerals" value={newEmployee.employer_charges_pct} onChange={(e) => setNewEmployee({ ...newEmployee, employer_charges_pct: e.target.value })} data-testid="new-emp-charges" /></td>
                <td><input type="number" step="0.5" className="input text-right numerals" value={newEmployee.hours_per_week} onChange={(e) => setNewEmployee({ ...newEmployee, hours_per_week: e.target.value })} data-testid="new-emp-hours" /></td>
                <td><input type="number" className="input text-right numerals" value={newEmployee.vacation_days} onChange={(e) => setNewEmployee({ ...newEmployee, vacation_days: e.target.value })} data-testid="new-emp-vacation" /></td>
                <td className="text-right text-secondary-text text-sm">auto</td>
                <td className="text-right">
                  <button onClick={addEmployee} className="btn btn-accent btn-sm" data-testid="add-employee-btn"><Plus size={14} /> Ajouter</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
      )}
    </div>
  );
}

function MicroRemunCard({ costEngine, onSaved }) {
  const [salary, setSalary] = useState("");
  const [hours, setHours] = useState("35");
  const [rate, setRate] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setSalary(costEngine.micro_target_salary ? String(costEngine.micro_target_salary) : "");
    setHours(costEngine.micro_billable_hours_week ? String(costEngine.micro_billable_hours_week) : "35");
    setRate(costEngine.micro_charges_pct != null ? String(costEngine.micro_charges_pct) : "");
  }, [costEngine.micro_target_salary, costEngine.micro_billable_hours_week, costEngine.micro_charges_pct]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/company", {
        micro_target_salary: Number(salary) || 0,
        micro_billable_hours_week: Number(hours) || 35,
        micro_charges_pct: Number(rate) || 0,
      });
      await onSaved();
      toast.success("Enregistré — taux horaire recalculé");
    } catch {
      toast.error("Échec de l'enregistrement");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card p-6 mb-8 animate-in delay-2" data-testid="micro-remun-card">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-lg bg-[var(--carbon-3)]/8 text-[var(--carbon-3)] flex items-center justify-center">
          <Users size={18} />
        </div>
        <div>
          <h2 className="font-semibold text-xl">Votre rémunération</h2>
          <p className="text-xs text-secondary-text mt-0.5">
            En auto-entrepreneur, pas de salariés : indiquez simplement ce que vous voulez vous verser chaque mois.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
        <Field label="Salaire mensuel envisagé (€)" testid="micro-target-salary">
          <input type="number" step="50" className="input numerals" value={salary}
            onChange={(e) => setSalary(e.target.value)} placeholder="ex. 2500" />
        </Field>
        <Field label="Heures facturables / semaine" testid="micro-billable-hours">
          <input type="number" step="0.5" className="input numerals" value={hours}
            onChange={(e) => setHours(e.target.value)} />
        </Field>
        <Field label="Taux cotisations URSSAF (%)" testid="micro-rate">
          <input type="number" step="0.1" className="input numerals" value={rate}
            onChange={(e) => setRate(e.target.value)} placeholder="ex. 21.2" />
        </Field>
        <button onClick={save} disabled={busy} className="btn btn-accent" data-testid="micro-remun-save">
          {busy ? "Enregistrement..." : "Enregistrer"}
        </button>
      </div>
      <div className="flex items-start gap-2 rounded-lg px-3 py-2.5 mt-4" data-testid="urssaf-rate-warning"
           style={{ background: "rgba(255,196,0,0.08)", border: "1px solid rgba(255,196,0,0.30)" }}>
        <AlertCircle size={14} style={{ color: "#FFC400", marginTop: 1, flexShrink: 0 }} />
        <div className="text-xs leading-relaxed" style={{ color: "var(--chalk)" }}>
          <span className="font-semibold" style={{ color: "#FFC400" }}>Vérifiez votre taux URSSAF</span> — il peut varier selon votre
          activité (prestations BIC 21,2 %, achat-revente 12,3 %, BNC 24,6 %), l'ACRE (taux réduit la 1ʳᵉ année) ou le versement
          libératoire. Référence : votre espace <span className="font-semibold">autoentrepreneur.urssaf.fr</span>.
        </div>
      </div>
      <div className="text-xs text-secondary-text mt-3 leading-relaxed">
        Calcul : (charges fixes {fmtEUR(costEngine.total_fixed_monthly)} + salaire envisagé {fmtEUR(costEngine.total_employee_cost_monthly)}) ÷ {costEngine.total_productive_hours_monthly} h/mois
        = {fmtEUR(costEngine.hourly_break_even_base)}/h, majoré des cotisations URSSAF ({String(costEngine.micro_charges_pct).replace(".", ",")} %)
        → <span className="font-semibold" style={{ color: "#FFC400" }}>seuil {fmtEUR(costEngine.hourly_break_even)}/h</span>.
        En dessous de ce taux, vous perdez de l'argent.
      </div>
    </section>
  );
}

function KpiCard({ label, value, hint, icon: Icon, accent, testid }) {
  return (
    <div className={`kpi ${accent ? "card-accent" : ""}`} data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="kpi-label">{label}</div>
        <Icon size={16} className="text-tertiary-text shrink-0" strokeWidth={1.6} />
      </div>
      <div className="kpi-value">{value}</div>
      {hint && <div className="text-xs text-tertiary-text font-body leading-snug">{hint}</div>}
    </div>
  );
}

function Field({ label, children, full, testid }) {
  return (
    <div className={full ? "md:col-span-2" : ""} data-testid={testid}>
      <label className="label">{label}</label>
      {children}
    </div>
  );
}

